import logging from 'utils/logging';
import { MOMENT_TYPES } from '../hostStateSlice';
import { GAMESTATE_INSTRUCTIONS } from '../../gamestate/constants';
import { advanceState } from '../../gamestate/GameState';
import { PLAYER_STATES, PLAYER_PHASES } from '../../player/playerConstants';
import { NARRATION_KEYS } from '../hostStateSlice';

const log = logging.getLogger('InstructionsDoneMoment');
log.setLevel('info');

const MOMENT_ID = 'instructions-done';

/**
 * The InstructionsReady trigger moment
 * Detects when all players are ready during the instructions phase
 */
export const InstructionsDoneTrigger = {
  id: MOMENT_ID,
  narrationKey: NARRATION_KEYS.INSTRUCTIONS_DONE,
  /**
   * Checks if all players are in the READY state for instructions
   * 
   * @param {object} params - The parameters for the moment
   * @param {object} params.gameState - The current game state
   * @param {array} params.currentPlayers - Array of player objects
   * @returns {boolean} - True if all players are ready
   */
  shouldTrigger: ({ currentState, currentPlayers }) => {
    log.debug('Checking if InstructionsDoneTrigger should trigger', currentState, currentPlayers);

    // Only trigger in the INSTRUCTIONS state
    if (currentState.currentState !== GAMESTATE_INSTRUCTIONS) {
      return false;
    }

    // If there are no players, don't trigger
    if (!currentPlayers || currentPlayers.length === 0) {
      log.debug('No players available, not triggering InstructionsReady');
      return false;
    }

    // Check if all players are in the READY state
    const allPlayersReady = currentPlayers.every(player => 
      player.playerState?.[PLAYER_PHASES.INSTRUCTIONS]?.state === PLAYER_STATES.INSTRUCTIONS.READY
    );

    if (allPlayersReady) {
      log.info('All players are ready for instructions!');
      
      const narrationKey = NARRATION_KEYS.INSTRUCTIONS_DONE;
      const playerNames = currentPlayers.map(player => player.name);

      return {
        shouldTriggerAction: false,
        shouldGenerateNarration: true,
        narrationParams: {
          narrationKey,
          momentType: MOMENT_TYPES.INSTRUCTIONS_DONE,
          context: {
            gameId: currentState.id,
            playerCount: currentPlayers?.length || 0,
            playerNames: playerNames,
            playerWithControl: currentPlayers[0].name, 
            allReady: true
          }
        }
      };
    }

    return false;
  }
};

/**
 * The InstructionsReady playback moment
 * Handles advancing the game state when all players are ready
 */
export const InstructionsDonePlayback = {
  id: `${MOMENT_ID}_PLAYBACK`,
  narrationKey: NARRATION_KEYS.INSTRUCTIONS_DONE,
  momentType: MOMENT_TYPES.INSTRUCTIONS_DONE,
  
  /**
   * Determines when this moment should play
   * 
   * @param {object} gameState - The current game state
   * @param {object} hostState - The current host state
   * @param {object} narration - The narration object
   * @param {set} playedNarrations - Set of narration keys that have been played
   * @returns {boolean} - True if the moment should play
   */
  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    // Skip if this narration has already been played
    if (playedNarrations.has(narration.key)) {
      return false;
    }
    
    // Only play if we're in the INSTRUCTIONS state
    const inInstructionsState = gameState.currentState === GAMESTATE_INSTRUCTIONS;
    
    // Check if narration exists and is ready
    const narrationIsReady = 
      narration?.textStatus === "READY" &&
      (narration?.audioStatus === "READY" || narration?.audioStatus === "ERROR");
    
    // Check if host is not speaking
    // const hostIsIdle = hostState.status !== HOST_STATUS.SPEAKING;
    
    // Make sure this is an instructions ready narration
    const isInstructionsDoneNarration = narration.key === NARRATION_KEYS.INSTRUCTIONS_DONE;
    
    log.debug("Should play InstructionsDone moment", inInstructionsState, narrationIsReady, isInstructionsDoneNarration);

    return inInstructionsState && narrationIsReady && isInstructionsDoneNarration;
  },
  
  /**
   * Determines if this moment should interrupt the current narration
   * 
   * @param {object} params - The parameters for interruption check
   * @param {object} params.gameState - The current game state
   * @param {object} params.hostState - The current host state
   * @param {array} params.players - Array of player objects
   * @param {array} params.narrations - Array of narration objects
   * @returns {boolean} - True if this moment should interrupt
   */
  shouldInterrupt: ({ gameState, hostState, players }) => {
    log.debug("Should interrupt InstructionsDone moment", gameState, hostState, players);
    // Only interrupt in the INSTRUCTIONS state
    if (gameState.currentState !== GAMESTATE_INSTRUCTIONS) {
      return false;
    }
    
    // Check if all players are ready
    if (!players || players.length === 0) {
      return false;
    }
    
    const allPlayersReady = players.every(player => 
      player.playerState?.[PLAYER_PHASES.INSTRUCTIONS]?.state === PLAYER_STATES.INSTRUCTIONS.READY
    );
    
    if (allPlayersReady) {
      log.info('InstructionsDone moment: All players are ready - should interrupt current narration');
      return true;
    }
    
    return false;
  },
  
  /**
   * Callback when the narration is complete
   * Advances the game state to the practice round
   * 
   * @param {string} gameId - The game ID
   * @param {function} dispatch - Redux dispatch function
   * @param {function} getState - Redux getState function
   */
  onComplete: async (gameId, dispatch, getState) => {
    log.info('Instructions done narration complete, advancing game state');
    
    try {
      // Get the current game object
      const state = getState();
      const gameObject = state.game?.game;
      
      if (!gameObject) {
        log.error('Game object not found in state');
        return;
      }
      
      // Get the current players
      const players = Object.values(state.player?.entities || {});
      
      // Advance the game state
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing game state:', error);
    }
  }
};

const api = {
  trigger: InstructionsDoneTrigger,
  playback: InstructionsDonePlayback
};

export default api;